import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { BankStatementDA } from '../../services/admin/bank.statement.service';
import { MediaDA } from '../../services/admin/media.service';
import { Company } from '../../models/company.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { modalHide } from '../../utils/modal.util';

export interface AppModalBankStatementImportProps {
  companyForUser: Company,
  treasuryAccountsForCompany: TreasuryAccount[],
  defaultTreasuryId?: string,
  onImported: (idBankStatement?: string) => void
};

type ImportSource = 'structured' | 'pdf_upload' | 'pdf_existing'

const POLL_INTERVAL = 2500
const POLL_TIMEOUT = 90000
const SEARCH_DEBOUNCE_MS = 400

const AppModalBankStatementImport: React.FunctionComponent<AppModalBankStatementImportProps> = ({companyForUser, treasuryAccountsForCompany, defaultTreasuryId, onImported}) => {
  const {lang} = StateLanguage()

  const [selectedTreasury, setSelectedTreasury] = useState(defaultTreasuryId ?? '')
  const selectedTreasuryRef = useRef(selectedTreasury)
  selectedTreasuryRef.current = selectedTreasury
  const [importSource, setImportSource] = useState<ImportSource>('structured')
  const [format, setFormat] = useState<'CAMT053' | 'CAMT054' | 'CSV'>('CAMT053')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // PDF existing document selector
  const [docs, setDocs] = useState<any[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [docSearch, setDocSearch] = useState('')
  const [docPage, setDocPage] = useState(1)
  const [docPagination, setDocPagination] = useState<{ total: number; pages: number; page: number }>({ total: 0, pages: 0, page: 1 })
  const [selectedDocId, setSelectedDocId] = useState<string>('')
  const [docStatus, setDocStatus] = useState<string>('')
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Extraction polling state
  const [extractionPhase, setExtractionPhase] = useState<'idle' | 'preparing' | 'extracting'>('idle')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartRef = useRef<number>(0)

  useEffect(() => {
    setSelectedTreasury(defaultTreasuryId ?? '')
  }, [defaultTreasuryId])

  // Load documents when switching to pdf_existing or changing page
  const loadDocuments = useCallback(async () => {
    if (!companyForUser) return
    setDocsLoading(true)
    try {
      const response = await MediaDA.getDocuments(
        companyForUser.id,
        docStatus,
        'uploaded_at',
        'desc',
        docPage,
        20,
        docSearch || undefined,
        undefined, undefined, undefined, undefined,
        'application/pdf',
        'done'
      )
      if (response.status === 200) {
        setDocs(response.data?.items ?? [])
        setDocPagination(response.data?.pagination ?? { total: 0, pages: 0, page: 1 })
      } else {
        setDocs([])
        setDocPagination({ total: 0, pages: 0, page: 1 })
      }
    } catch {
      setDocs([])
      setDocPagination({ total: 0, pages: 0, page: 1 })
    } finally {
      setDocsLoading(false)
    }
  }, [companyForUser, docPage, docSearch, docStatus])

  useEffect(() => {
    if (importSource === 'pdf_existing') {
      loadDocuments()
    }
  }, [importSource, docPage, loadDocuments])

  // Debounced search
  const handleDocSearchChange = (value: string) => {
    setDocSearch(value)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDocPage(1)
    }, SEARCH_DEBOUNCE_MS)
  }

  // Cleanup polling + search timer on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    }
  }, [])

  const handleFileSelect = (selectedFile: File | null) => {
    setFile(selectedFile)
    setError(null)

    if (selectedFile && importSource === 'structured') {
      const name = selectedFile.name.toLowerCase()
      if (name.endsWith('.csv')) {
        setFormat('CSV')
      } else {
        setFormat('CAMT053')
      }
    }
  }

  // Poll OCR status until done, then trigger extraction
  const waitForOcrThenExtract = useCallback(async (documentId: string) => {
    setExtractionPhase('preparing')
    pollStartRef.current = Date.now()

    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT) {
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = null
        setExtractionPhase('idle')
        setImporting(false)
        setError(lang.labels.extractionTimeout)
        return
      }

      try {
        const docRes = await MediaDA.getDocument(companyForUser.id, documentId)
        const doc = docRes.data
        const ocrStatus = doc?.ocr_status ?? doc?.status

        if (ocrStatus === 'done' || ocrStatus === 'completed' || ocrStatus === 'processed') {
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = null
          triggerExtraction(documentId)
        } else if (ocrStatus === 'failed' || ocrStatus === 'error') {
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = null
          setExtractionPhase('idle')
          setImporting(false)
          setError(lang.labels.extractionFailed)
        }
      } catch {
        // Network error during poll — keep trying until timeout
      }
    }, POLL_INTERVAL)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser.id, lang])

  const triggerExtraction = async (documentId: string) => {
    const treasuryId = selectedTreasuryRef.current
    if (!treasuryId) {
      setExtractionPhase('idle')
      setImporting(false)
      setError(lang.labels.treasuryAccountRequired ?? 'Please select a treasury account')
      return
    }
    setExtractionPhase('extracting')

    try {
      const res = await BankStatementDA.createFromDocument(companyForUser.id, treasuryId, documentId)

      if (res.status === 200 || res.status === 201) {
        const statementId = res.data?.id ?? res.data?.id_bank_statement
        if (statementId) {
          pollExtractionStatus(statementId)
        } else {
          modalHide('modal-bank-statement-import')
          resetForm()
          onImported()
        }
      } else {
        setExtractionPhase('idle')
        setImporting(false)
        setError(res.data?.message ?? lang.labels.extractionFailed)
      }
    } catch (err: any) {
      setExtractionPhase('idle')
      setImporting(false)
      setError(err.message ?? lang.labels.extractionFailed)
    }
  }

  const pollExtractionStatus = (statementId: string) => {
    setExtractionPhase('extracting')
    pollStartRef.current = Date.now()

    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT) {
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = null
        setExtractionPhase('idle')
        setImporting(false)
        setError(lang.labels.extractionTimeout)
        return
      }

      try {
        const res = await BankStatementDA.getStatement(companyForUser.id, statementId)
        if (res.status !== 200) return

        const job = res.data?.extraction_job
        const status = job?.status ?? res.data?.import_status

        if (status === 'completed' || status === 'done') {
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = null
          setExtractionPhase('idle')
          setImporting(false)
          modalHide('modal-bank-statement-import')
          resetForm()
          onImported(statementId)
        } else if (status === 'failed' || status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = null
          setExtractionPhase('idle')
          setImporting(false)
          setError(job?.error ?? lang.labels.extractionFailed)
        }
      } catch {
        // Keep polling until timeout
      }
    }, POLL_INTERVAL)
  }

  const handleImport = async () => {
    if (!selectedTreasury) {
      setError(lang.labels.treasuryAccountRequired ?? 'Please select a treasury account')
      return
    }

    setImporting(true)
    setError(null)

    if (importSource === 'structured') {
      if (!file) return
      try {
        const response = await BankStatementDA.importStatement(companyForUser.id, selectedTreasury, file, format)

        if (response.status === 200 || response.status === 201) {
          modalHide('modal-bank-statement-import')
          resetForm()
          onImported()
        } else {
          setError(response.data?.message ?? lang.labels.importFailed)
        }
      } catch (err: any) {
        setError(err.message ?? 'Unknown error')
      } finally {
        setImporting(false)
      }
    } else if (importSource === 'pdf_upload') {
      if (!file) return
      try {
        const uploadRes = await MediaDA.uploadDocumentSmart(companyForUser.id, file, 'web')

        if (uploadRes.status === 201 || uploadRes.status === 200) {
          const docId = uploadRes.data?.id ?? uploadRes.data?.id_document_inbox
          if (docId) {
            waitForOcrThenExtract(docId)
          } else {
            setImporting(false)
            setError(lang.labels.importFailed)
          }
        } else {
          setImporting(false)
          setError(uploadRes.data?.message ?? lang.labels.importFailed)
        }
      } catch (err: any) {
        setImporting(false)
        setError(err.message ?? 'Unknown error')
      }
    } else if (importSource === 'pdf_existing') {
      if (!selectedDocId) return
      triggerExtraction(selectedDocId)
    }
  }

  const resetForm = () => {
    setFile(null)
    setFormat('CAMT053')
    setError(null)
    setImportSource('structured')
    setSelectedDocId('')
    setDocStatus('')
    setDocs([])
    setDocSearch('')
    setDocPage(1)
    setDocPagination({ total: 0, pages: 0, page: 1 })
    setExtractionPhase('idle')
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const handleClose = () => {
    if (importing) return
    modalHide('modal-bank-statement-import')
    resetForm()
  }

  const isSubmitDisabled = () => {
    if (!selectedTreasury || importing) return true
    if (importSource === 'structured') return !file
    if (importSource === 'pdf_upload') return !file
    if (importSource === 'pdf_existing') return !selectedDocId
    return true
  }

  const getSubmitLabel = () => {
    if (extractionPhase === 'preparing') return lang.labels.preparingDocument
    if (extractionPhase === 'extracting') return lang.labels.extractingStatement
    return lang.labels.importAction
  }

  const fileAccept = importSource === 'structured' ? '.xml,.csv' : '.pdf'

  return (
    <div id="modal-bank-statement-import" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.importBankStatement}</h2>
            <button type="button" className="btn-close" onClick={handleClose} disabled={importing}></button>
          </div>
          <div className="modal-body">
            <div className="fv-row mb-5">
              <label className="form-label required">{lang.labels.treasuryAccount}</label>
              <Form.Select bsPrefix="form-select" value={selectedTreasury} onChange={(e) => setSelectedTreasury(e.target.value)} disabled={importing}>
                <option value="">{lang.labels.selectOption}</option>
                { treasuryAccountsForCompany.map((item: TreasuryAccount, index: number) => (
                  <option key={index} value={item.id}>{item.name}{item.iban ? ` — ${item.iban}` : ''}</option>
                ))}
              </Form.Select>
            </div>
            <div className="fv-row mb-5">
              <label className="form-label required">{lang.labels.source ?? 'Source'}</label>
              <Form.Select bsPrefix="form-select" value={importSource} onChange={(e) => { setImportSource(e.target.value as ImportSource); setFile(null); setError(null); setSelectedDocId('') }} disabled={importing}>
                <option value="structured">{lang.labels.importSourceStructured}</option>
                <option value="pdf_upload">{lang.labels.importSourcePdfUpload}</option>
                <option value="pdf_existing">{lang.labels.importSourcePdfExisting}</option>
              </Form.Select>
            </div>
            { importSource === 'structured' &&
              <div className="fv-row mb-5">
                <label className="form-label required">{lang.labels.format}</label>
                <Form.Select bsPrefix="form-select" value={format} onChange={(e) => setFormat(e.target.value as any)} disabled={importing}>
                  <option value="CAMT053">CAMT.053 (Bank statement)</option>
                  <option value="CAMT054">CAMT.054 (Credit notification)</option>
                  <option value="CSV">CSV</option>
                </Form.Select>
              </div>
            }
            { (importSource === 'structured' || importSource === 'pdf_upload') &&
              <div className="fv-row mb-5">
                <label className="form-label required">{lang.labels.file}</label>
                <div
                  className={`border border-2 border-dashed rounded p-5 text-center ${dragOver ? 'border-primary bg-light-primary' : 'border-gray-300'} ${importing ? 'opacity-50' : 'cursor-pointer'}`}
                  onDragOver={(e) => { e.preventDefault(); if (!importing) setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); if (importing) return; const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f) }}
                  onClick={() => { if (importing) return; document.getElementById('bank-statement-file-input')?.click() }}
                >
                  { file
                    ?
                    <div>
                      <span className="svg-icon svg-icon-2x text-primary">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                          <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                        </svg>
                      </span>
                      <div className="fw-bold mt-2">{file.name}</div>
                      <small className="text-muted">{(file.size / 1024).toFixed(1)} KB</small>
                    </div>
                    :
                    <div>
                      <span className="svg-icon svg-icon-2x text-muted">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="black" />
                          <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="black" />
                          <path d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="#C4C4C4" />
                        </svg>
                      </span>
                      <div className="mt-2">{lang.labels.dragAndDropOrClick}</div>
                    </div>
                  }
                </div>
                <input id="bank-statement-file-input" type="file" style={{display: 'none'}} accept={fileAccept} onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)} />
              </div>
            }
            { importSource === 'pdf_existing' &&
              <div className="fv-row mb-5">
                <label className="form-label required">{lang.labels.selectExistingDocument}</label>
                <div className="d-flex gap-3 mb-3">
                  <input
                    type="text"
                    className="form-control form-control-sm flex-grow-1"
                    placeholder={lang.labels.searchDocuments}
                    value={docSearch}
                    onChange={(e) => handleDocSearchChange(e.target.value)}
                    disabled={importing}
                  />
                  <Form.Select
                    bsPrefix="form-select form-select-sm"
                    style={{width: '180px', flexShrink: 0}}
                    value={docStatus}
                    onChange={(e) => { setDocStatus(e.target.value); setDocPage(1) }}
                    disabled={importing}
                  >
                    <option value="">{lang.labels.allStatuses}</option>
                    <option value="new">{lang.labels.statusNew}</option>
                    <option value="assigned">{lang.labels.statusAssigned}</option>
                    <option value="partially-processed">{lang.labels.statusPartiallyProcessed}</option>
                    <option value="processed">{lang.labels.statusProcessed}</option>
                  </Form.Select>
                </div>
                { docsLoading
                  ?
                  <div className="text-center py-5">
                    <span className="spinner-border spinner-border-sm align-middle"></span>
                  </div>
                  : docs.length > 0
                    ?
                    <>
                      <div className="border rounded" style={{maxHeight: '280px', overflowY: 'auto'}}>
                        <table className="table table-row-bordered table-row-dashed align-middle mb-0">
                          <thead className="bg-light">
                            <tr className="text-muted text-uppercase fw-bolder fs-8">
                              <th className="ps-3">{lang.labels.name}</th>
                              <th className="text-nowrap">{lang.labels.uploadedAt}</th>
                              <th className="text-center">{lang.labels.status}</th>
                              <th className="w-30px"></th>
                            </tr>
                          </thead>
                          <tbody>
                            { docs.map((doc: any) => (
                              <tr
                                key={doc.id}
                                className={`cursor-pointer ${selectedDocId === doc.id ? 'bg-light-primary' : ''}`}
                                onClick={() => { if (!importing) setSelectedDocId(doc.id) }}
                              >
                                <td className="ps-3">
                                  <div className="d-flex align-items-center">
                                    <span className="svg-icon svg-icon-2 me-2 text-danger flex-shrink-0">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="currentColor" />
                                        <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="currentColor" />
                                      </svg>
                                    </span>
                                    <span className="fw-bold text-truncate" style={{maxWidth: '250px'}}>{doc.original_name ?? doc.name ?? 'PDF'}</span>
                                  </div>
                                </td>
                                <td className="text-nowrap text-muted small">
                                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}
                                </td>
                                <td className="text-center">
                                  <span className={`badge badge-light-${doc.status === 'new' ? 'info' : doc.status === 'assigned' ? 'primary' : doc.status === 'partially-processed' ? 'warning' : 'success'}`}>
                                    {doc.status === 'new' ? lang.labels.statusNew : doc.status === 'assigned' ? lang.labels.statusAssigned : doc.status === 'partially-processed' ? lang.labels.statusPartiallyProcessed : doc.status === 'processed' ? lang.labels.statusProcessed : (doc.status ?? '—')}
                                  </span>
                                </td>
                                <td className="text-center pe-3">
                                  { selectedDocId === doc.id &&
                                    <span className="svg-icon svg-icon-2 text-primary">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.02396 16.4147C9.54916 16.9768 10.4346 16.9768 10.9769 16.4147L18.1429 8.97318C18.6246 8.46108 18.6246 7.66259 18.1429 7.15049C17.6177 6.59219 16.7305 6.59219 16.2053 7.15049L9.89557 13.4982Z" fill="currentColor" />
                                      </svg>
                                    </span>
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      { docPagination.pages > 1 &&
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <button
                            className="btn btn-sm btn-light"
                            disabled={docPage <= 1}
                            onClick={() => setDocPage(p => Math.max(1, p - 1))}
                          >
                            {lang.labels.previous}
                          </button>
                          <small className="text-muted">
                            {(lang.labels.pageXofN ?? 'Page {page} of {pages}')
                              .replace('{page}', String(docPage))
                              .replace('{pages}', String(docPagination.pages))}
                          </small>
                          <button
                            className="btn btn-sm btn-light"
                            disabled={docPage >= docPagination.pages}
                            onClick={() => setDocPage(p => Math.min(docPagination.pages, p + 1))}
                          >
                            {lang.labels.next}
                          </button>
                        </div>
                      }
                    </>
                    :
                    <div className="text-center text-muted py-5">
                      {lang.labels.noPdfDocumentsAvailable}
                    </div>
                }
              </div>
            }
            { error &&
              <div className="alert alert-danger py-3">
                <span className="fw-bold">{error}</span>
              </div>
            }
            { extractionPhase !== 'idle' &&
              <div className="alert alert-info py-3 d-flex align-items-center">
                <span className="spinner-border spinner-border-sm me-3"></span>
                <span>{extractionPhase === 'preparing' ? lang.labels.preparingDocument : lang.labels.extractingStatement}</span>
              </div>
            }
            { importSource === 'structured' &&
              <div className="alert alert-primary py-3">
                <small>{lang.labels.autoMatchInfo}</small>
              </div>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={handleClose} disabled={importing}>{lang.labels.cancel}</button>
            <button className="btn btn-primary mx-2" type="button" onClick={handleImport} disabled={isSubmitDisabled()} data-kt-indicator={importing ? 'on' : 'off'}>
              <span className="indicator-label">{getSubmitLabel()}</span>
              <span className="indicator-progress">
                {getSubmitLabel()}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalBankStatementImport;
